import { useRef, useState } from 'react';
import type * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import type { LinkSceneProps, SceneProps } from '.';
import { deg2rad } from '../../../_utils/deg2rad';
import { roundedLerp } from '../../../_utils/roundedLerp';
import { roundedRotationLerp } from '../../../_utils/roundedRotationLerp';
import { Marker } from './Marker';
import type { ScrollSceneAnimationControls } from '../../../ThreeLayout/ScrollScene';
import { useSaveCustomCursor } from '../../../../CustomCursor';

type headsetRefsType = {
	group: React.RefObject<THREE.Group>;
	headset: React.RefObject<THREE.Group>;
	headband: React.RefObject<THREE.Mesh>;
};

export function HeadsetModel(props: SceneProps & ScrollSceneAnimationControls) {
	const saveCustomCursor = useSaveCustomCursor();

	const headsetRefs = useRef<headsetRefsType>({
		group: useRef(null),
		headset: useRef(null),
		headband: useRef(null),
	}).current;

	const [markerVisible, setMarkerVisible] = useState<boolean>(true);

	const hover = useRef({
		allowOut: true,
	}).current;

	useOnClickAnimation(headsetRefs, props.modelsInfo);

	useFrame(() => {
		if (headsetRefs.headset.current && headsetRefs.headband.current && window) {
			/**
			 * If there is no active product allow hover events
			 */
			if (!props.modelsInfo.activeProduct) {
				if (props.sections.headsetHover.current) {
					if (props.hover.hoverObject === 'headset') {
						if (props.sections.headsetHover.current.style.pointerEvents !== 'auto') {
							props.sections.headsetHover.current.style.pointerEvents = 'auto';
						}
					}
					if (props.hover.hoverObject !== 'headset') {
						if (props.sections.headsetHover.current.style.pointerEvents !== 'none') {
							props.sections.headsetHover.current.style.pointerEvents = 'none';
						}
					}
				}

				if (!markerVisible) setMarkerVisible(true);
			} else {
				/**
				 * reset hover if there is an active product
				 */
				if (props.sections.headsetHover.current) {
					if (props.sections.headsetHover.current.style.pointerEvents !== 'none') {
						props.sections.headsetHover.current.style.pointerEvents = 'none';
					}
				}

				if (markerVisible) setMarkerVisible(false);
			}
		}
	});

	return (
		<>
			{props.overlines?.headset && (
				<Marker {...{ position: [-14, 9.5, 9] }} style={{ opacity: markerVisible ? 1 : 0 }}>
					{props.overlines.headset}
					<div className={`h-8 origin-top-left mt-4`}>
						<div className={`w-px h-8 bg-linear-to-b from-coral-100 to-coral-100/0`} />
					</div>
				</Marker>
			)}
			<mesh
				scale={3}
				{...{ position: [-8, 6, 12.1] }}
				onPointerMove={event => {
					event.stopPropagation();
					event.stopPropagation();
					if (props.hover.hoverObject !== 'headset' && !props.modelsInfo.activeProduct) {
						props.hover.hoverObject = 'headset';
						props.hover.rotation.x = 3;
						props.hover.rotation.y = 5;
						props.hover.scale = 1.05;
						hover.allowOut = false;
						setTimeout(() => {
							hover.allowOut = true;
							saveCustomCursor({
								size: 'large',
								children: (
									<>
										{props.learnMore?.learn}
										<br />
										{props.learnMore?.more}
									</>
								),
							});
						}, 100);
					}
				}}
				onPointerLeave={event => {
					event.stopPropagation();
					if (props.hover.hoverObject === 'headset' && hover.allowOut) {
						saveCustomCursor({ size: 'small', children: <></> });
						props.hover.hoverObject = 'none';
						props.hover.rotation.x = 0;
						props.hover.rotation.y = 0;
						props.hover.scale = 0;
					}
				}}
				onWheel={event => {
					event.stopPropagation();
					if (props.hover.hoverObject === 'headset' && hover.allowOut) {
						saveCustomCursor({ size: 'small', children: <></> });
						props.hover.hoverObject = 'none';
						props.hover.rotation.x = 0;
						props.hover.rotation.y = 0;
						props.hover.scale = 0;
					}
				}}
				onClick={event => {
					event.stopPropagation();
					if (!props.modelsInfo.activeProduct && props.sections.modal.current) {
						saveCustomCursor({ size: 'small', children: <></> });
						props.setNewProductByName('Headset');
						props.scrollSceneAnimationControls.setSmoothPositioning('waiting for start');
						setTimeout(() => {
							props.scrollSceneAnimationControls.setAllowScrollAnimation(false);
							props.scrollSceneAnimationControls.setNewTrackerWrapper(props.sections.modal);
						}, 100);
					}
				}}
			>
				<planeGeometry {...{ args: [4, 4] }} />
				<meshBasicMaterial {...{ color: 'white', transparent: true, opacity: 0 }} />
			</mesh>
			<group ref={headsetRefs.group}>
				{props.scene && (
					<>
						<primitive
							ref={headsetRefs.headband}
							{...{ position: [-4.8, 0, 12.1], rotation: [0, deg2rad(-33), 0], object: props.scene.Headband_Main }}
						/>
						<group
							ref={headsetRefs.headset}
							{...{
								position: [headsetProps.x, headsetProps.y, headsetProps.z],
								rotation: [deg2rad(headsetProps.rotationX), deg2rad(headsetProps.rotationY), deg2rad(headsetProps.rotationZ)],
							}}
						>
							<primitive {...{ position: [0, 0, 0], rotation: [0, 0, 0], object: props.scene.Headband_Headset }} />
							<mesh
								{...{
									position: [0, 0, 0],
									rotation: [0, deg2rad(60), 0],
									geometry: props.scene.Headset.geometry,
									material: props.scene.HeadsetFloorMaterial,
								}}
							/>
						</group>
					</>
				)}
			</group>
		</>
	);
}

function useOnClickAnimation(headsetRefs: headsetRefsType, modelsInfo: LinkSceneProps['modelsInfo']) {
	const animation = useRef({ needReset: false, lastProductPosition: 0 }).current;

	useFrame((_, d) => {
		if (window && headsetRefs.group.current) {
			const orientation = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
			const ratio = orientation === 'portrait' ? (window.innerHeight / window.innerWidth) * 0.65 : 1;

			const delta = d / (animation.lastProductPosition === 0 ? 1 : animation.lastProductPosition + 0.1);
			if (modelsInfo.activeProduct && modelsInfo.products) {
				if (animation.lastProductPosition !== modelsInfo.products.Headset.position) {
					animation.lastProductPosition = Math.abs(modelsInfo.products.Headset.position);
				}

				animation.needReset = true;
				/**
				 * scene group scale animation
				 */
				const scaleX = roundedLerp(headsetRefs.group.current.scale.x, headsetGroupProps[orientation].scale, delta, 5);
				if (scaleX !== headsetRefs.group.current.scale.x) {
					headsetRefs.group.current.scale.set(scaleX, scaleX, scaleX);
				}
				/**
				 * scene group rotation animation
				 */
				const rotationX = roundedRotationLerp(headsetRefs.group.current.rotation.x, headsetGroupProps.rotationX, delta, 5);
				if (rotationX !== headsetRefs.group.current.rotation.x) {
					headsetRefs.group.current.rotation.x = rotationX;
				}
				const rotationY = roundedRotationLerp(headsetRefs.group.current.rotation.y, headsetGroupProps.rotationY, delta, 5);
				if (rotationY !== headsetRefs.group.current.rotation.y) {
					headsetRefs.group.current.rotation.y = rotationY;
				}
				const rotationZ = roundedRotationLerp(headsetRefs.group.current.rotation.z, headsetGroupProps.rotationZ, delta, 5);
				if (rotationZ !== headsetRefs.group.current.rotation.z) {
					headsetRefs.group.current.rotation.z = rotationZ;
				}

				/**
				 * scene group position animation
				 */

				const positionX = roundedLerp(
					headsetRefs.group.current.position.x,
					headsetGroupProps[orientation].x + modelsInfo.products.Headset.position * 55,
					delta,
					5,
				);
				if (positionX !== headsetRefs.group.current.position.x) {
					headsetRefs.group.current.position.x = positionX;
				} else {
					if (modelsInfo.products.Headset.position !== 0) {
						headsetRefs.group.current.visible = false;
					}
				}

				if (modelsInfo.products.Headset.position === 0) {
					headsetRefs.group.current.visible = true;
				} else {
					const offscreenPosition = modelsInfo.products.Headset.position * 50;
					if (offscreenPosition < 0 && Math.abs(offscreenPosition) < Math.abs(headsetRefs.group.current.position.x))
						headsetRefs.group.current.visible = false;
					if (offscreenPosition > 0 && offscreenPosition < headsetRefs.group.current.position.x + 6)
						headsetRefs.group.current.visible = false;
				}

				const positionY = roundedLerp(headsetRefs.group.current.position.y, headsetGroupProps[orientation].y * ratio, delta, 5);
				if (positionY !== headsetRefs.group.current.position.y) {
					headsetRefs.group.current.position.y = positionY;
				}
			}

			if (animation.needReset && !modelsInfo.activeProduct) {
				headsetRefs.group.current.visible = true;

				/**
				 * scene group scale reset
				 */
				const scale = roundedLerp(headsetRefs.group.current.scale.x, 1, delta, 5);
				if (scale !== headsetRefs.group.current.scale.x) {
					headsetRefs.group.current.scale.set(scale, scale, scale);
				}

				/**
				 * scene group rotation reset
				 */
				const rotationX = roundedRotationLerp(headsetRefs.group.current.rotation.x, 0, delta, 5);
				if (rotationX !== headsetRefs.group.current.rotation.x) {
					headsetRefs.group.current.rotation.x = rotationX;
				}
				const rotationY = roundedRotationLerp(headsetRefs.group.current.rotation.y, 0, delta, 5);
				if (rotationY !== headsetRefs.group.current.rotation.y) {
					headsetRefs.group.current.rotation.y = rotationY;
				}
				const rotationZ = roundedRotationLerp(headsetRefs.group.current.rotation.z, 0, delta, 5);
				if (rotationZ !== headsetRefs.group.current.rotation.z) {
					headsetRefs.group.current.rotation.z = rotationZ;
				}

				/**
				 * scene group position reset
				 */

				const positionX = roundedLerp(headsetRefs.group.current.position.x, 0, delta, 5);
				if (positionX !== headsetRefs.group.current.position.x) {
					headsetRefs.group.current.position.x = positionX;
				}

				const positionY = roundedLerp(headsetRefs.group.current.position.y, 0, delta, 5);
				if (positionY !== headsetRefs.group.current.position.y) {
					headsetRefs.group.current.position.y = positionY;
				}

				if (
					Number(headsetRefs.group.current.scale.x.toFixed(2)) === 1 &&
					Number(headsetRefs.group.current.position.x.toFixed(2)) === 0 &&
					Number(headsetRefs.group.current.position.y.toFixed(2)) === 0 &&
					Number(headsetRefs.group.current.rotation.z.toFixed(2)) === 0 &&
					Number(headsetRefs.group.current.rotation.y.toFixed(2)) === 0 &&
					Number(headsetRefs.group.current.rotation.x.toFixed(2)) === 0
				) {
					headsetRefs.group.current.scale.set(1, 1, 1);
					headsetRefs.group.current.rotation.set(0, 0, 0);
					headsetRefs.group.current.position.set(0, 0, 0);
					animation.needReset = false;
					animation.lastProductPosition = 0;
				}
			}
		}
	});
}

const headsetGroupProps = {
	landscape: { x: -4.6, y: 9, scale: 0.85 },
	portrait: { x: 3, y: 17, scale: 1.6 },
	rotationX: 26,
	rotationY: -13,
	rotationZ: -55,
};

const headsetProps = {
	x: -4.2,
	y: 2.2,
	z: 14.9,
	rotationX: 61,
	rotationY: 86,
	rotationZ: 0,
};
